import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { CheckCircle2, AlertTriangle, XCircle, RefreshCw } from "lucide-react";
import { API } from "@/api";
import { useContent } from "@/content/ContentContext";
import { MAIN_PAGES, SUBURB_PAGES, SEO_IDEAL } from "@/seoPages";

function checkLength(value, min, max) {
  const text = (value || "").trim();
  if (!text) return { status: "bad", note: "Missing" };
  if (text.length < min) return { status: "warn", note: `Too short (${text.length})` };
  if (text.length > max) return { status: "warn", note: `Too long (${text.length})` };
  return { status: "good", note: `Good (${text.length})` };
}

const STATUS_STYLE = {
  good: { icon: CheckCircle2, pill: "bg-sage-light text-sage-dark", label: "Good" },
  warn: { icon: AlertTriangle, pill: "bg-amber-100 text-amber-700", label: "Check" },
  bad: { icon: XCircle, pill: "bg-red-100 text-red-600", label: "Fix" },
};

function StatusPill({ status }) {
  const s = STATUS_STYLE[status];
  const Icon = s.icon;
  return (
    <span className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[11px] font-bold uppercase tracking-wide ${s.pill}`}>
      <Icon className="h-3 w-3" aria-hidden="true" />
      {s.label}
    </span>
  );
}

export default function SeoHealthTab() {
  const { get } = useContent();
  const [gallery, setGallery] = useState(null);

  const loadGallery = () => {
    axios
      .get(`${API}/gallery`)
      .then((res) => setGallery(Array.isArray(res.data) ? res.data : []))
      .catch(() => setGallery([]));
  };

  useEffect(loadGallery, []);

  const pages = useMemo(() => {
    return [...MAIN_PAGES, ...SUBURB_PAGES].map((p) => {
      const title = p.titleKey ? get(p.titleKey, p.defaultTitle) : p.defaultTitle;
      const desc = p.descKey ? get(p.descKey, p.defaultDesc) : p.defaultDesc;
      const titleCheck = checkLength(title, SEO_IDEAL.titleMin, SEO_IDEAL.titleMax);
      const descCheck = checkLength(desc, SEO_IDEAL.descMin, SEO_IDEAL.descMax);
      const worst = [titleCheck, descCheck].some((c) => c.status === "bad")
        ? "bad"
        : [titleCheck, descCheck].some((c) => c.status === "warn")
          ? "warn"
          : "good";
      return { ...p, title, desc, titleCheck, descCheck, worst };
    });
  }, [get]);

  const missingAlt = useMemo(() => (gallery || []).filter((g) => !(g.alt || "").trim()), [gallery]);
  const pagesWithIssues = pages.filter((p) => p.worst !== "good").length;
  const galleryLoaded = gallery !== null;

  return (
    <div data-testid="seo-health-tab" className="space-y-8">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="max-w-2xl text-sm leading-relaxed text-ink/60">
          A health check of every public page's search title and description, plus photo alt-text coverage. Fix titles
          and descriptions in the <strong>Content &amp; SEO</strong> tab; fix photo alt text in the <strong>Gallery</strong> tab.
        </p>
        <button
          type="button"
          onClick={loadGallery}
          data-testid="seo-health-refresh"
          className="inline-flex min-h-[44px] items-center gap-2 rounded-full border border-line bg-white px-5 text-xs font-bold tracking-[0.1em] text-ink/70 transition-colors duration-200 hover:border-sage hover:text-charcoal"
        >
          <RefreshCw className="h-3.5 w-3.5" aria-hidden="true" />
          RE-CHECK
        </button>
      </div>

      <div className="grid gap-4 sm:grid-cols-3">
        <div data-testid="seo-summary-pages" className="rounded-2xl border border-line bg-white p-5">
          <p className="text-xs font-bold uppercase tracking-[0.15em] text-ink/50">Pages checked</p>
          <p className="mt-2 font-display text-3xl font-extrabold text-charcoal">{pages.length}</p>
        </div>
        <div data-testid="seo-summary-issues" className="rounded-2xl border border-line bg-white p-5">
          <p className="text-xs font-bold uppercase tracking-[0.15em] text-ink/50">Pages needing attention</p>
          <p className={`mt-2 font-display text-3xl font-extrabold ${pagesWithIssues ? "text-amber-600" : "text-sage-dark"}`}>
            {pagesWithIssues}
          </p>
        </div>
        <div data-testid="seo-summary-alt" className="rounded-2xl border border-line bg-white p-5">
          <p className="text-xs font-bold uppercase tracking-[0.15em] text-ink/50">Photos missing alt text</p>
          <p className={`mt-2 font-display text-3xl font-extrabold ${!galleryLoaded ? "text-ink/40" : missingAlt.length ? "text-red-500" : "text-sage-dark"}`}>
            {galleryLoaded ? missingAlt.length : "…"}
          </p>
        </div>
      </div>

      <div className="overflow-hidden rounded-2xl border border-line bg-white">
        <div className="border-b border-line px-5 py-4">
          <h3 className="font-display text-lg font-bold text-charcoal">Page titles &amp; descriptions</h3>
          <p className="mt-1 text-xs text-ink/50">
            Ideal title: {SEO_IDEAL.titleMin}–{SEO_IDEAL.titleMax} characters · Ideal description: {SEO_IDEAL.descMin}–{SEO_IDEAL.descMax} characters
          </p>
        </div>
        <ul className="divide-y divide-line">
          {pages.map((p, i) => (
            <li key={p.route} data-testid={`seo-page-row-${i}`} className="flex flex-col gap-3 px-5 py-4 sm:flex-row sm:items-center sm:justify-between">
              <div className="min-w-0">
                <p className="font-display text-base font-bold text-charcoal">{p.label}</p>
                <p className="truncate text-xs text-ink/45">{p.route}</p>
              </div>
              <div className="flex flex-wrap items-center gap-x-5 gap-y-2 text-xs">
                <span className="inline-flex items-center gap-2">
                  <StatusPill status={p.titleCheck.status} />
                  <span className="font-semibold text-ink/60">Title: {p.titleCheck.note}</span>
                </span>
                <span className="inline-flex items-center gap-2">
                  <StatusPill status={p.descCheck.status} />
                  <span className="font-semibold text-ink/60">Description: {p.descCheck.note}</span>
                </span>
              </div>
            </li>
          ))}
        </ul>
      </div>

      <div className="overflow-hidden rounded-2xl border border-line bg-white">
        <div className="border-b border-line px-5 py-4">
          <h3 className="font-display text-lg font-bold text-charcoal">Photo alt text</h3>
          <p className="mt-1 text-xs text-ink/50">
            Alt text helps Google understand your installation photos and can bring in image-search traffic.
          </p>
        </div>
        <div className="px-5 py-4">
          {!galleryLoaded ? (
            <p className="text-sm text-ink/50">Checking gallery photos…</p>
          ) : missingAlt.length === 0 ? (
            <p data-testid="seo-alt-all-good" className="inline-flex items-center gap-2 text-sm font-semibold text-sage-dark">
              <CheckCircle2 className="h-4 w-4" aria-hidden="true" />
              All {gallery.length} gallery photos have alt text. Nice work.
            </p>
          ) : (
            <ul className="space-y-2">
              {missingAlt.map((g, i) => (
                <li key={g.src || i} data-testid={`seo-alt-missing-${i}`} className="inline-flex items-center gap-2 text-sm font-semibold text-red-600">
                  <XCircle className="h-4 w-4" aria-hidden="true" />
                  {(g.src || "Uploaded photo").split("/").pop()} — missing alt text
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}
